import { useSearchParams } from "react-router-dom";

/**
 * Mirrors a component's UI state in the URL query string, so Back/Forward restore it
 * and a link survives a refresh.
 *
 * The caller declares which keys belong in the URL by passing their **defaults**, and
 * the default's type doubles as the parser — so there's no second schema to keep in
 * step, and adding a key later is one word:
 *
 *     const [state, setState] = useUrlState({ page: 1, view: "grid" });
 *
 *     state.page                            // number; 1 when absent or junk
 *     setState({ page: 3 });
 *     setState({ view: "list", page: 1 });  // one navigation, not two
 *
 * `number` defaults parse the param and fall back when it isn't finite, `boolean`
 * defaults compare against `"true"`, anything else is taken as a string. Values are
 * only ever coerced towards the default's type, never validated against a set — if a
 * key has a fixed vocabulary, narrow it at the call site.
 *
 * Keys outside the schema are ignored on read and left untouched on write, so several
 * hooks (or a parent and a child) can share one query string without clobbering each
 * other.
 *
 * A patch whose values already match the URL is skipped. Without that, "resetting" to
 * the state you're already in — `setState({ page: 1 })` while on page 1, which is what
 * a filter change does — would stack history entries with identical URLs and make Back
 * look broken.
 *
 * **A value equal to its schema default leaves the URL instead of sitting in it.**
 * Clearing a filter must not strand `?brands=` as an empty husk, and the common state
 * (`page=1`, `view=grid`, no filters) should be the bare path — shareable URLs carry
 * only what differs. Reading treats an absent key and a default-valued key identically,
 * so deletion is invisible to consumers.
 *
 * `set` takes `{ replace: true }` for the one thing that isn't a user step: **correcting
 * an invalid value**. Pushing there traps the user — Back returns to the bad value,
 * which corrects itself again, and Back/Forward bounce. Every real interaction pushes.
 *
 * @param {Record<string, string|number|boolean>} schema Key → default value.
 * @returns {[Record<string, any>, (patch: object, opts?: {replace?: boolean}) => void]}
 */
const parseParam = (raw, fallback) => {
  if (raw == null || raw === "") return fallback;
  if (typeof fallback === "number") {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  if (typeof fallback === "boolean") return raw === "true";
  return raw;
};

export default function useUrlState(schema) {
  const [params, setParams] = useSearchParams();

  const state = {};
  Object.entries(schema).forEach(([key, fallback]) => {
    state[key] = parseParam(params.get(key), fallback);
  });

  const set = (patch, { replace = false } = {}) => {
    // null serialisation = "this key should be absent": nullish values and values equal
    // to the schema default both delete rather than write.
    const serialize = (key, value) =>
      value == null || String(value) === String(schema[key])
        ? null
        : String(value);

    const entries = Object.entries(patch).map(([key, value]) => [
      key,
      serialize(key, value),
    ]);
    if (entries.every(([key, value]) => (params.get(key) ?? null) === value)) {
      return;
    }
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        entries.forEach(([key, value]) => {
          if (value == null) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        });
        return next;
      },
      { replace }
    );
  };

  return [state, set];
}
